"use strict";
import { TradeType, CurrencyAmount, Percent, Price, Fraction } from "@uniswap/sdk-core";
import { ONE } from "@uniswap/router-sdk";
import { useCallback, useMemo, useState } from "react";
import { useAccountMeta } from "contexts/UniswapContext";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { useCurrencyInfo } from "features/tokens/useCurrencyInfo";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
import { useQuery } from "@tanstack/react-query";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import {
  fetch0xPrice,
  fetch0xQuote,
  ZERO_X_NATIVE_TOKEN
} from "features/routing/api/zeroXApi";
import { QuoteMethod } from "state/routing/types";
function create0xTradeObject(response, currencyIn, currencyOut, tradeType, slippageTolerance) {
  const inputAmount = CurrencyAmount.fromRawAmount(currencyIn, response.sellAmount);
  const outputAmount = CurrencyAmount.fromRawAmount(currencyOut, response.buyAmount);
  const minBuyAmount = response.minBuyAmount ? CurrencyAmount.fromRawAmount(currencyOut, response.minBuyAmount) : outputAmount;
  const executionPrice = new Price(
    currencyIn,
    currencyOut,
    inputAmount.quotient.toString(),
    outputAmount.quotient.toString()
  );
  const slippagePercent = new Percent(Math.floor(slippageTolerance * 100), 1e4);
  return {
    inputAmount,
    outputAmount,
    tradeType,
    executionPrice,
    minimumAmountOut(_slippage) {
      if (tradeType === TradeType.EXACT_OUTPUT) {
        return outputAmount;
      }
      if (!_slippage && minBuyAmount) {
        return minBuyAmount;
      }
      const st = _slippage || slippagePercent;
      const adjusted = new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyOut, adjusted);
    },
    maximumAmountIn(slippage) {
      const st = slippage || slippagePercent;
      if (tradeType === TradeType.EXACT_INPUT) {
        return inputAmount;
      }
      const adjusted = new Fraction(ONE).add(st).multiply(inputAmount.quotient).quotient;
      return CurrencyAmount.fromRawAmount(currencyIn, adjusted);
    },
    worstExecutionPrice(slippage) {
      const st = slippage || slippagePercent;
      const minOut = tradeType === TradeType.EXACT_OUTPUT ? outputAmount : minBuyAmount || CurrencyAmount.fromRawAmount(
        currencyOut,
        new Fraction(ONE).subtract(st).multiply(outputAmount.quotient).quotient
      );
      return new Price(currencyIn, currencyOut, inputAmount.quotient.toString(), minOut.quotient.toString());
    },
    inputTax: new Percent(0),
    outputTax: new Percent(0),
    priceImpact: void 0,
    gasUseEstimate: response.gas ? parseInt(response.gas, 10) : 2e5,
    gasUseEstimateUSD: "0.01",
    quoteMethod: QuoteMethod.CLIENT_SIDE_FALLBACK,
    approveInfo: { needsApprove: false },
    routing: "CLASSIC",
    fillType: "classic",
    is0x: true,
    zeroXResponse: response,
    v2Routes: [],
    v3Routes: [],
    mixedRoutes: [],
    swaps: [],
    routes: [],
    postSwapFeeOutputAmount: outputAmount,
    swapFee: void 0,
    blockNumber: void 0,
    requestId: void 0,
    totalGasUseEstimateUSD: void 0,
    slippageTolerance,
    slippageTolerancePercent: slippagePercent
  };
}
export function use0xQuote({
  tokenIn,
  tokenOut,
  amountSpecified,
  tradeType,
  chainId,
  skip = false,
  currencyIn: currencyInOverride,
  currencyOut: currencyOutOverride
}) {
  const account = useAccountMeta();
  const { defaultChainId } = useEnabledChains();
  const effectiveChainId = chainId ?? defaultChainId;
  const { customSlippageTolerance, autoSlippageTolerance } = useTransactionSettingsContext();
  const slippageValue = customSlippageTolerance ?? autoSlippageTolerance ?? 0.5;
  const slippageBps = Math.floor(slippageValue * 100);
  const currencyInInfo = useCurrencyInfo(
    tokenIn && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenIn) : void 0
  );
  const currencyOutInfo = useCurrencyInfo(
    tokenOut && effectiveChainId ? buildCurrencyId(effectiveChainId, tokenOut) : void 0
  );
  const currencyIn = currencyInOverride ?? currencyInInfo?.currency;
  const currencyOut = currencyOutOverride ?? currencyOutInfo?.currency;
  const sellToken = currencyIn?.isNative ? ZERO_X_NATIVE_TOKEN : tokenIn;
  const buyToken = currencyOut?.isNative ? ZERO_X_NATIVE_TOKEN : tokenOut;
  const shouldSkip = useMemo(
    () => skip || !tokenIn || !tokenOut || !amountSpecified || amountSpecified === "0" || !currencyIn || !currencyOut || tokenIn === tokenOut || tradeType !== TradeType.EXACT_INPUT,
    [skip, tokenIn, tokenOut, amountSpecified, currencyIn, currencyOut, tradeType]
  );
  const hasTaker = Boolean(account?.address);
  const [refreshKey, setRefreshKey] = useState(0);
  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);
  const {
    data,
    isLoading,
    error,
    dataUpdatedAt
  } = useQuery({
    queryKey: [
      "0x-quote",
      effectiveChainId,
      sellToken,
      buyToken,
      amountSpecified,
      slippageBps,
      hasTaker,
      account?.address,
      refreshKey
    ],
    queryFn: async () => {
      if (!sellToken || !buyToken || !amountSpecified) {
        return null;
      }
      const params = {
        chainId: effectiveChainId,
        sellToken,
        buyToken,
        sellAmount: amountSpecified,
        slippageBps,
        taker: account?.address
      };
      if (account?.address) {
        return fetch0xQuote(params);
      }
      return fetch0xPrice(params);
    },
    enabled: !shouldSkip,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1
  });
  const quoteUpdatedAt = dataUpdatedAt || null;
  const trade = useMemo(() => {
    if (!data || !currencyIn || !currencyOut) {
      return null;
    }
    return create0xTradeObject(data, currencyIn, currencyOut, tradeType, slippageValue);
  }, [data, currencyIn, currencyOut, tradeType, slippageValue]);
  return useMemo(
    () => ({
      trade,
      isLoading,
      error,
      refetch,
      quoteUpdatedAt
    }),
    [trade, isLoading, error, refetch, quoteUpdatedAt]
  );
}
